import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { FRAMED_PLACEHOLDER_MIN_HEIGHT, fullPageMinHeight } from '../lib/embedResize';

interface AppErrorBoundaryProps {
  children: React.ReactNode;
  /** Clears a caught error when it changes, e.g. the current pathname, so navigating away recovers. */
  resetKey?: string;
  /**
   * "app" (default): the last-resort, whole-screen fallback. "page": sits inside the app layout
   * around the routed page, so the sidebar and top bar survive a page crash.
   */
  variant?: 'app' | 'page';
}

interface AppErrorBoundaryState {
  error: Error | null;
}

/** Lazy route chunks disappear after a deploy; a reload fetches the new build. */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return error.name === 'ChunkLoadError'
    || /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Loading chunk [\w-]+ failed/i.test(error.message);
}

/**
 * Error boundary for the admin app: the last resort around everything, and a page-level one
 * around the routed page (variant "page"). It deliberately carries no product branding so the
 * fallback also works inside white-label embeds.
 */
export class AppErrorBoundary extends React.Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  override state: AppErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): AppErrorBoundaryState {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('Unhandled error in the app', error, info.componentStack);
  }

  override componentDidUpdate(previous: AppErrorBoundaryProps) {
    if (this.state.error && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  private readonly reload = () => {
    window.location.reload();
  };

  override render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const staleBuild = isChunkLoadError(error);
    const pageLevel = this.props.variant === 'page';
    return (
      <Box
        role="alert"
        display="flex"
        alignItems="center"
        justifyContent="center"
        // A page-level fallback sits in the layout's content area: no viewport height (embeds).
        minHeight={pageLevel ? FRAMED_PLACEHOLDER_MIN_HEIGHT : fullPageMinHeight() ?? FRAMED_PLACEHOLDER_MIN_HEIGHT}
        px={2}
        py={pageLevel ? 6 : 0}
        sx={{ bgcolor: pageLevel ? 'transparent' : 'background.default', color: 'text.primary' }}
      >
        <Stack spacing={2} maxWidth={440} textAlign="center" alignItems="center">
          <Typography variant="h6" fontWeight={700}>
            {staleBuild ? 'A new version is available' : 'Something went wrong'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {staleBuild
              ? 'This page was updated while you had it open. Reload to continue.'
              : 'This page hit an unexpected error. Changes that were already saved are safe — reload to try again. If it keeps happening, contact support.'}
          </Typography>
          <Button variant="contained" onClick={this.reload}>
            Reload page
          </Button>
        </Stack>
      </Box>
    );
  }
}
